import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  CheckCircle, 
  AlertCircle, 
  Eye, 
  EyeOff, 
  Loader2,
  PartyPopper,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Verify the invite token on load
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No invitation token provided');
        setVerifying(false);
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/admin/auth/invite/verify/${token}`);
        
        if (response.data.valid) {
          setInvite(response.data);
        } else {
          setError(response.data.message || 'Invalid or expired invitation');
        }
      } catch (err) {
        setError('Unable to verify invitation. It may have expired.');
      } finally {
        setVerifying(false);
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  // Validate password
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    setPasswordErrors(validatePassword(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (passwordErrors.length > 0) {
      setError('Please fix password requirements');
      return;
    }
    
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/api/admin/auth/invite/accept`, {
        token,
        name: name.trim(),
        password
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#F2D857] animate-spin mx-auto" />
          <p className="text-gray-400 mt-4">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid/expired invitation
  if (!invite && !success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-gray-900 border-gray-800 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Invitation</h1>
          <p className="text-gray-400 mb-6">
            {error || 'This invitation link is invalid or has expired.'}
          </p>
          <Button
            onClick={() => navigate('/admin/login')}
            className="bg-[#F2D857] text-black hover:bg-[#F2D857]/90"
          >
            Go to Admin Login
          </Button>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-gray-900 border-gray-800 text-center">
          <PartyPopper className="h-16 w-16 text-[#F2D857] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Welcome to the Team! 🎉</h1>
          <p className="text-gray-400 mb-6">
            Your admin account has been created successfully. You can now log in.
          </p>
          <Button
            onClick={() => navigate('/admin/login')}
            className="bg-[#F2D857] text-black hover:bg-[#F2D857]/90 w-full"
          >
            Log In Now
          </Button>
        </Card>
      </div>
    );
  }

  // Account setup form
  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 bg-gray-900 border-gray-800">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-4xl mb-2">🎤</div>
          <h1 className="text-2xl font-bold text-[#F2D857]">Mic Check Millionaire</h1>
          <p className="text-gray-400 mt-2">Set up your admin account</p>
        </div>

        {/* Invite info */}
        <div className="bg-[#F2D857]/10 border border-[#F2D857]/30 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-300">
            <span className="text-[#F2D857] font-medium">Email:</span> {invite.email}
          </p>
          <p className="text-sm text-gray-300 mt-1">
            <span className="text-[#F2D857] font-medium">Role:</span> {invite.role}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-gray-300">Your Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="mt-1 bg-gray-800 border-gray-700 text-white"
              required
            />
          </div>

          {/* Password */}
          <div>
            <Label htmlFor="password" className="text-gray-300">Create Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder="Create a strong password"
                className="bg-gray-800 border-gray-700 text-white pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            
            {/* Password requirements */}
            {password && (
              <div className="mt-2 space-y-1">
                {['At least 12 characters', 'One uppercase letter', 'One lowercase letter', 'One number', 'One special character'].map((req) => {
                  const isMet = !passwordErrors.includes(req);
                  return (
                    <div key={req} className={`text-xs flex items-center gap-1 ${isMet ? 'text-green-500' : 'text-gray-500'}`}>
                      {isMet ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-gray-500" />}
                      {req}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className="mt-1 bg-gray-800 border-gray-700 text-white"
              required
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </p>
            </div>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            disabled={submitting || passwordErrors.length > 0 || password !== confirmPassword || !name.trim()}
            className="w-full bg-[#F2D857] text-black hover:bg-[#F2D857]/90 font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-2" />
                Create My Account
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default AcceptInvite;
